import React, { useState, useMemo } from 'react';
import { Calendar, DollarSign, CheckCircle, Zap } from 'lucide-react';
import {
  calculatePaymentSchedule,
  SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  SLICE_PAY_HOLD_FEE_RATE,
} from '@/utils/paymentScheduleCalculator';
import { format, parseISO } from 'date-fns';
import { BRAND_SELECTED_TOGGLE_CLASS } from '@/constants/brandColors';
import { fmtAmount } from '@/utils/money';

/**
 * @param {number} [lastPaymentDaysBeforeDeparture] - Slice 14 / Capital 7 / Zero 21 (Review passes explicitly). Ignored when isRefundableFare (Zero uses 21).
 * @param {boolean} [deferaCapitalDepositTiers] - Defera Capital deposit bands + show deposit as $ on review.
 * @param {number} [holdFeeRate] - Fraction of pre-hold subtotal (Slice vs Capital). Default: Slice 15%.
 */
export default function PaymentSchedule({
  bookingDate,
  departureDate,
  totalWithFee,
  onSelectionChange,
  isRefundableFare = false,
  firstPaymentDays,
  lastPaymentDaysBeforeDeparture,
  deferaCapitalDepositTiers = false,
  holdFeeRate = SLICE_PAY_HOLD_FEE_RATE,
  /** Faye travel insurance premium (dollars): joins the payments after the
   *  deposit, never the deposit or the hold fee. */
  insurancePremium = 0,
}) {
  // Default to the lowest tier: refundable=0%, Defera Capital=5%, Slice Pay=20%.
  // Close-in trips where the lowest tier isn't offered snap up via the depositOptions sync effect below.
  // NOTE: deferaCapitalDepositTiers can flip false→true after mount when the async checkout-provider
  // hook resolves; until the user manually picks a tier, the auto-snap effect below keeps us on the
  // lowest available option even after that prop change.
  const [selectedDeposit, setSelectedDeposit] = useState(
    isRefundableFare ? 0 : (deferaCapitalDepositTiers ? 5 : 20)
  );
  const [selectedPlan, setSelectedPlan] = useState('weekly'); // 'weekly' or 'biweekly'
  // Tracks whether the user has manually picked a deposit tier; once true, the auto-snap-to-lowest
  // effect stops overriding their selection (but the not-in-options snap-up still runs as a backstop).
  const userPickedDepositRef = React.useRef(false);
  
  // When refundable, force 0% and don't update from parent
  React.useEffect(() => {
    if (isRefundableFare) {
      setSelectedDeposit(0);
    }
  }, [isRefundableFare]);
  
  // Notify parent of selection changes
  React.useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange({ deposit: selectedDeposit, plan: selectedPlan });
    }
  }, [selectedDeposit, selectedPlan, onSelectionChange]);
  
  // Calculate schedule based on selected deposit (0% when refundable)
  const schedule = useMemo(() => {
    if (!bookingDate || !departureDate || !totalWithFee || totalWithFee <= 0) {
      return null;
    }
    
    return calculatePaymentSchedule({
      bookingDate,
      departureDate,
      totalWithFee,
      depositPercentage: isRefundableFare ? 0 : selectedDeposit,
      firstPaymentDays: isRefundableFare ? 7 : firstPaymentDays,
      daysBeforeDepartureForLastPayment: isRefundableFare
        ? DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE
        : (lastPaymentDaysBeforeDeparture ?? SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE),
      deferaCapitalDepositTiers: !isRefundableFare && deferaCapitalDepositTiers,
      holdFeeRate,
      insurancePremium: isRefundableFare ? 0 : insurancePremium,
    });
  }, [bookingDate, departureDate, totalWithFee, selectedDeposit, isRefundableFare, firstPaymentDays, lastPaymentDaysBeforeDeparture, deferaCapitalDepositTiers, holdFeeRate, insurancePremium]);

  // Until the user manually picks a tier, keep snapping to the lowest available option whenever
  // depositOptions changes. This catches the case where deferaCapitalDepositTiers flips false→true
  // after mount (async checkout-provider hook) and a lower tier (e.g. 5%) becomes available.
  React.useEffect(() => {
    if (isRefundableFare) return;
    if (userPickedDepositRef.current) return;
    if (!schedule?.depositOptions?.length) return;
    const lowest = schedule.depositOptions[0];
    if (selectedDeposit !== lowest) {
      setSelectedDeposit(lowest);
    }
  }, [schedule?.depositOptions, isRefundableFare, selectedDeposit]);

  // Backstop: if the current selection is no longer in depositOptions (e.g. trip moved close-in
  // and the lowest tier is hidden), snap to whatever the calculator chose.
  React.useEffect(() => {
    if (isRefundableFare) return;
    if (!schedule?.available || !schedule.depositOptions?.length) return;
    if (!schedule.depositOptions.includes(selectedDeposit) && schedule.depositPercentage != null) {
      setSelectedDeposit(schedule.depositPercentage);
    }
  }, [schedule?.available, schedule?.depositOptions, schedule?.depositPercentage, isRefundableFare, selectedDeposit]);
  
  // If no schedule available (insufficient lead time), show message
  if (!schedule || !schedule.available) {
    return (
      <div className="pt-6">
        <p className="text-white/85 text-sm font-normal text-center">
          {schedule?.reason === 'insufficient_lead_time' 
            ? `Payment plans are only available for trips booked at least 22 days before departure. Your trip is ${schedule.daysUntilDeparture} days away.`
            : 'Payment schedule is not available for this booking.'}
        </p>
      </div>
    );
  }
  
  const activePlan = selectedPlan === 'weekly' ? schedule.weeklyPlan : schedule.biweeklyPlan;
  
  return (
    <div className="pt-2 pb-6">
      {/* Deposit Selection - hide for refundable fares (0% down) */}
      {!isRefundableFare && (
        <div className="mb-10">
          <label className="block text-lg font-medium text-white mb-4">
            Select your deposit amount
          </label>
          <div className="flex gap-2 px-1 md:px-0">
            {schedule.depositOptions.map((percent) => {
              const depositDollars = Math.round(schedule.totalBeforeHoldFee * (percent / 100) * 100) / 100;
              const label = deferaCapitalDepositTiers
                ? Math.ceil(depositDollars).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                : `${percent}%`;
              return (
              <button
                key={percent}
                onClick={() => {
                  userPickedDepositRef.current = true;
                  setSelectedDeposit(percent);
                }}
                className={`flex-1 px-2 py-2.5 md:px-4 md:py-3 rounded-full text-xs md:text-sm font-medium transition-all ${
                  selectedDeposit === percent
                    ? BRAND_SELECTED_TOGGLE_CLASS
                    : 'bg-white/10 text-white/80 hover:bg-white/20 border border-transparent'
                }`}
              >
                {label}
              </button>
              );
            })}
          </div>
        </div>
      )}
      {isRefundableFare && (
        <div className="mb-10 flex items-center justify-between gap-4">
          <span className="text-3xl font-semibold text-white tracking-tight">0% Down</span>
          <span className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full overflow-hidden shadow-[0_0_16px_-4px_rgba(234,88,12,0.3)]">
            <span className="absolute inset-0 bg-gradient-to-r from-red-800/30 via-orange-600/35 to-orange-500/30" />
            <span className="absolute inset-0 bg-[linear-gradient(135deg,transparent_0%,rgba(251,146,60,0.08)_50%,rgba(185,28,28,0.06)_100%)]" />
            <span className="absolute inset-[1px] rounded-full border border-red-900/30" />
            <span className="relative flex items-center gap-2 text-white text-sm font-medium">
              <Zap className="w-4 h-4 drop-shadow-[0_0_4px_rgba(251,146,60,0.5)]" />
              Defera Zero
            </span>
          </span>
        </div>
      )}
      
      {/* Payment Plan Selection - Side by side on desktop */}
      <div className="mb-10">
        <label className="block text-lg font-medium text-white mb-4">
          Choose your payment plan
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Weekly Plan */}
          {schedule.weeklyPlan.available && (
            <button
              onClick={() => setSelectedPlan('weekly')}
              className={`relative px-6 py-4 rounded-full text-left transition-all border ${
                selectedPlan === 'weekly'
                  ? BRAND_SELECTED_TOGGLE_CLASS
                  : 'bg-white/10 text-white/80 hover:bg-white/20 border-transparent'
              }`}
            >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-base mb-1">Weekly</div>
                          <div className="text-sm font-medium opacity-80">
                            ${fmtAmount(schedule.weeklyPlan.paymentAmount)} x {schedule.weeklyPlan.paymentCount}
                          </div>
                        </div>
                {selectedPlan === 'weekly' && (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                )}
              </div>
            </button>
          )}
          
          {/* Bi-weekly Plan */}
          {schedule.biweeklyPlan.available && (
            <button
              onClick={() => setSelectedPlan('biweekly')}
              className={`relative px-6 py-4 rounded-full text-left transition-all border ${
                selectedPlan === 'biweekly'
                  ? BRAND_SELECTED_TOGGLE_CLASS
                  : 'bg-white/10 text-white/80 hover:bg-white/20 border-transparent'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-base mb-1">Bi-weekly</div>
                  <div className="text-sm font-medium opacity-80">
                    ${fmtAmount(schedule.biweeklyPlan.paymentAmount)} x {schedule.biweeklyPlan.paymentCount}
                  </div>
                </div>
                {selectedPlan === 'biweekly' && (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                )}
              </div>
            </button>
          )}
        </div>
      </div>
      
      {/* Payment Schedule List - All payments visible, no scroll */}
      {activePlan && activePlan.available && activePlan.schedule.length > 0 && (
        <div className="mb-4 mt-10">
          <label className="block text-lg font-medium text-white mb-4">
            Upcoming Payments
          </label>
          <div className="space-y-2">
            {activePlan.schedule.map((dateStr, index) => {
              const date = parseISO(dateStr);
              const isFirst = index === 0;
              const isLast = index === activePlan.schedule.length - 1;
              
              return (
                <div
                  key={dateStr}
                  className="flex items-center justify-between py-2.5 px-4 rounded-full bg-white/5"
                >
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-4 h-4 flex-shrink-0 text-white/40" />
                      <div className="text-sm font-medium text-white">
                        {format(date, 'EEE, MMM d, yyyy')}
                      </div>
                    </div>
                  <div className="text-sm font-medium text-white">
                    ${fmtAmount(activePlan.paymentAmount)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

